// Global client state management service
//
// Workspace tabs: every tab owns a `ClientStateService` scope (which client it
// has open). The client *data* lives once in the shared `ClientStore`, keyed by
// client id, so two tabs on the same client see each other's edits instantly.
// `useClientState()` reads the scope of the tab it renders in
// (`ClientScopeContext`); outside a tab — and through the default export — it
// uses the active tab's scope.
import React from 'react';
import { createClientRecord, updateClientRecord, getClientRecord, removeUndefinedValues } from './firestoreService';
import { recordMaterialChanges } from './materialChangeAudit';
import { routeFormDataUpdates } from '../utils/adviceScenarioPayload';
import { stampAdviceUpdates } from '../utils/adviceRowLinks';
import { bootstrapAdviceJourneyForClient } from './adviceJourneyBootstrap';
import { isClientListRow, markClientListRow } from '../utils/clientListRow';
import { addLiveClient, dropLiveClientPatch, patchLiveClient, removeLiveClient } from './liveClientPatches';

// Copies of a portal list row stay marked as one (see clientListRow.js).
const keepListRowMark = (from, to) => (isClientListRow(from) ? markClientListRow(to) : to);

// Dev server only: a hot update to this file or anything it imports re-runs it,
// while workspace tabs keep their scope objects in React state. The new copy
// reuses the store, active scope and context, or those tabs' clicks would update
// a store no page listens to (the page stops following the selection until a
// refresh). Production builds have no `module.hot`.
const hot = typeof module !== 'undefined' ? module.hot : undefined;
const carried = (hot && hot.data && hot.data.clientState) || null;

function applyDotNotation(target, updates) {
  const result = { ...target };
  for (const [key, value] of Object.entries(updates)) {
    if (key.includes('.')) {
      const parts = key.split('.');
      let obj = result;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...(obj[parts[i]] || {}) };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
    } else {
      result[key] = value;
    }
  }
  return result;
}

/** @returns {number} Firestore `lastModified` as comparable ms (0 if missing). */
function getLastModifiedMillis(client) {
  if (!client) return 0;
  const lm = client.lastModified;
  if (lm == null) return 0;
  if (typeof lm.toMillis === 'function') return lm.toMillis();
  if (typeof lm.seconds === 'number') {
    return lm.seconds * 1000 + (typeof lm.nanoseconds === 'number' ? lm.nanoseconds / 1e6 : 0);
  }
  return 0;
}

/**
 * True if `remote` should replace in-memory `local` (newer or material server change).
 * Avoids clobbering optimistic state that is still ahead of the last server timestamp.
 */
function shouldMergeRemoteClientDoc(local, remote) {
  if (!remote) return false;
  if (!local) return true;
  const r = getLastModifiedMillis(remote);
  const l = getLastModifiedMillis(local);
  if (r > l) return true;
  if (r < l) return false;
  if (local.workflowStatus !== remote.workflowStatus) return true;
  if (!!local.implementationConfirmed !== !!remote.implementationConfirmed) return true;
  return false;
}

/**
 * Shared by every tab: the realtime client list and one live record
 * (`{ selectedClient, clientData }`) per open client id, reference-counted by
 * the scopes holding it. Arrays and records are replaced, never mutated, so
 * subscribers can compare by reference.
 */
class ClientStore {
  constructor() {
    this.allClients = [];
    this.clientsLoaded = false;
    this.records = new Map();
    this.listeners = new Set();
    this.version = 0;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  notify() {
    this.version += 1;
    this.listeners.forEach(listener => listener());
  }

  getVersion = () => this.version;

  subscribeVersion = (listener) => this.subscribe(listener);

  getRecord(clientId) {
    return clientId ? this.records.get(clientId) || null : null;
  }

  acquire(clientId) {
    let record = this.records.get(clientId);
    if (!record) {
      record = { selectedClient: null, clientData: null, refs: 0 };
      this.records.set(clientId, record);
    }
    record.refs += 1;
    return record;
  }

  release(clientId) {
    const record = this.records.get(clientId);
    if (!record) return;
    record.refs -= 1;
    if (record.refs <= 0) this.records.delete(clientId);
  }

  /** Ids of every client open in some tab (for per-client realtime listeners). */
  getOpenClientIds() {
    return Array.from(this.records.keys());
  }

  replaceInAllClients(clientId, mapFn) {
    if (!Array.isArray(this.allClients)) return;
    const idx = this.allClients.findIndex(c => c.id === clientId);
    if (idx < 0) return;
    const next = [...this.allClients];
    next[idx] = mapFn(next[idx]);
    this.allClients = next;
  }

  /** Merge a full client document into its open record when the server copy is newer. */
  mergeRecordFromRemote(fresh) {
    const record = this.getRecord(fresh?.id);
    if (!record) return;
    if (!shouldMergeRemoteClientDoc(record.clientData, fresh)) return;
    record.clientData = { ...fresh };
    record.selectedClient = { ...fresh };
  }

  applyServerClientDocument(fresh) {
    if (!fresh?.id) return;
    if (this.allClients?.length) {
      this.replaceInAllClients(fresh.id, () => ({ ...fresh }));
    }
    this.mergeRecordFromRemote(fresh);
    this.notify();
  }

  setAllClients(clients) {
    this.allClients = clients;
    this.clientsLoaded = true;
    for (const id of this.records.keys()) {
      const row = clients.find(c => c.id === id);
      if (row) this.mergeRecordFromRemote(row);
    }
    this.notify();
  }

  clearClientCache() {
    this.allClients = [];
    this.clientsLoaded = false;
    this.notify();
  }

  removeClientFromCache(clientId) {
    if (Array.isArray(this.allClients)) {
      this.allClients = this.allClients.filter(client => client.id !== clientId);
      this.notify();
    }
  }
}

export const clientStore = carried?.clientStore || new ClientStore();

/** One tab's view of client state: which client it has open. */
class ClientStateService {
  constructor(store = clientStore) {
    this.store = store;
    this.clientId = null;
  }

  get selectedClient() {
    return this.store.getRecord(this.clientId)?.selectedClient ?? null;
  }

  get clientData() {
    return this.store.getRecord(this.clientId)?.clientData ?? null;
  }

  get allClients() {
    return this.store.allClients;
  }

  get clientsLoaded() {
    return this.store.clientsLoaded;
  }

  // Subscribe to client state changes
  subscribe(listener) {
    return this.store.subscribe(() => listener(this.getState()));
  }

  // Notify all listeners of state changes
  notify() {
    this.store.notify();
  }

  // Select a client and load their data
  async selectClient(client) {
    const clientId = client.id;
    const previousId = this.clientId;
    if (previousId !== clientId) {
      this.store.acquire(clientId);
      this.clientId = clientId;
      if (previousId) this.store.release(previousId);
    }
    const record = this.store.getRecord(clientId);
    // Re-selecting an already-open client (this tab's tab-activation refresh, or
    // the same client open in another tab): keep the current optimistic data
    // visible so a slow/stale fetch can't clobber local edits that haven't
    // reached the server yet.
    const isReselect = record.clientData != null;
    if (!isReselect) {
      // Clear old data immediately to prevent stale data issues
      record.clientData = null;
      record.selectedClient = client;
    } else if (shouldMergeRemoteClientDoc(record.selectedClient, client)) {
      record.selectedClient = client;
    }

    // Notify immediately to force UI reset
    this.notify();

    // Fetch fresh data from database to ensure we have the latest changes
    try {
      const result = await getClientRecord(clientId);
      // Every tab closed this client while the fetch was in flight.
      if (this.store.getRecord(clientId) !== record) return;
      if (result.success && result.client) {
        if (!isReselect || shouldMergeRemoteClientDoc(record.clientData, result.client)) {
          record.clientData = result.client;
          // Picked from a portal list: swap the slim row for the full doc.
          if (isClientListRow(record.selectedClient)) record.selectedClient = result.client;
          console.log('Client data refreshed from database');
        } else {
          console.log('Kept local client data (fetched copy is not newer)');
        }
      } else if (!record.clientData && !isClientListRow(client)) {
        // Fallback to cached data if database fetch fails (never a slim list
        // row: pages would read, and could save, a client with no data)
        record.clientData = client;
        console.log('Using cached client data (database fetch failed)');
      }
    } catch (error) {
      console.error('Error fetching fresh client data:', error);
      if (this.store.getRecord(clientId) !== record) return;
      // Fallback to cached data
      if (!record.clientData && !isClientListRow(client)) {
        record.clientData = client;
      }
    }

    // Notify again with fresh data
    this.notify();
  }

  // Clear client selection
  clearSelection() {
    if (this.clientId) this.store.release(this.clientId);
    this.clientId = null;
    this.notify();
  }

  /** Release this scope's hold on its client (its workspace tab closed). */
  dispose() {
    if (this.clientId) this.store.release(this.clientId);
    this.clientId = null;
  }

  getLastModifiedMillis(client) {
    return getLastModifiedMillis(client);
  }

  shouldMergeRemoteClientDoc(local, remote) {
    return shouldMergeRemoteClientDoc(local, remote);
  }

  /**
   * Merge a single full client document from the realtime `allClients` list into the
   * open client when the server row is newer or materially changed.
   */
  mergeSelectedFromRemoteListRow(fresh) {
    this.store.mergeRecordFromRemote(fresh);
  }

  /**
   * Apply a full client document from Firestore (per-client onSnapshot) or
   * equivalent: keep `allClients` in sync and refresh every open copy of that
   * client when appropriate so other users' implementation confirms are visible
   * while a tab still has the checklist open.
   */
  applyServerClientDocument(fresh) {
    this.store.applyServerClientDocument(fresh);
  }

  // Set all clients (for caching)
  setAllClients(clients) {
    this.store.setAllClients(clients);
  }

  // Get all clients from cache
  getAllClients() {
    return this.store.allClients;
  }

  // Check if clients are loaded
  areClientsLoaded() {
    return this.store.clientsLoaded;
  }

  // Clear client cache (for refresh)
  clearClientCache() {
    this.store.clearClientCache();
  }

  // Remove a client from the cache
  removeClientFromCache(clientId) {
    this.store.removeClientFromCache(clientId);
    removeLiveClient(clientId);
  }

  // Get current state
  getState() {
    return {
      selectedClient: this.selectedClient,
      clientData: this.clientData,
      allClients: this.allClients,
      clientsLoaded: this.clientsLoaded
    };
  }

  // Create a new client with just name; optional assignment overrides (e.g. adviser self-assign on referral create).
  async createNewClient(userId, firstName, surname, options = {}) {
    try {
      const minimalData = {
        personal: {
          firstName: firstName?.trim(),
          surname: surname?.trim(),
        },
        ...(options.assignment ? { assignment: options.assignment } : {}),
      };

      const result = await createClientRecord(userId, minimalData);

      if (result.success) {
        addLiveClient(result.clientRecord);
        // Select the newly created client
        this.selectClient(result.clientRecord);

        bootstrapAdviceJourneyForClient({
          clientId: result.clientId,
          clientData: result.clientRecord,
          userEmail: userId,
        }).catch((err) => {
          console.error('Advice Journey bootstrap on client create failed:', err);
        });

        // Add the new client to the cache only if not already present (subscription may have already updated)
        if (Array.isArray(this.store.allClients)) {
          const alreadyInList = this.store.allClients.some(c => c.id === result.clientRecord.id);
          if (!alreadyInList) {
            this.store.allClients = [result.clientRecord, ...this.store.allClients];
          }
          this.notify();
        }

        return { success: true, client: result.clientRecord };
      }

      return result;
    } catch (error) {
      console.error('Error creating new client:', error);
      return { success: false, error: error.message };
    }
  }

  async updateClientData(updates, userId = null, options = undefined) {
    if (!this.selectedClient) {
      throw new Error('No client selected');
    }
    // Updates stay pinned to this client even if the tab moves on before the save lands.
    return this.updateClientDataForClient(this.clientId, updates, userId, options);
  }

  // Update a specific client by id (e.g. claim from table without selecting). Persists to Firestore;
  // optimistic update of the list row and of the client's open record (shared by every tab on it).
  async updateClientDataForClient(clientId, rawUpdates, userId = null, options = undefined) {
    const store = this.store;
    const record = store.getRecord(clientId);
    // Advice written here records the ids of the fund / cover rows it points at (Stage 4, Oct 2026).
    const updates = stampAdviceUpdates(rawUpdates, record?.clientData || null);
    const listRow = Array.isArray(store.allClients)
      ? store.allClients.find(c => c.id === clientId) || null
      : null;
    const previousListRow = listRow ? { ...listRow } : null;
    const previousSelectedClient = record?.selectedClient
      ? keepListRowMark(record.selectedClient, { ...record.selectedClient })
      : null;
    const previousClientData = record?.clientData ? { ...record.clientData } : null;

    // Audit material fact find / recommendations / amendments changes.
    // Values are snapshotted synchronously against the pre-update state; the
    // coalescer writes at most one entry per area per editing burst.
    try {
      recordMaterialChanges({
        clientId,
        userId,
        updates,
        previousData: record ? record.clientData : listRow,
      });
    } catch (auditError) {
      console.error('Material change audit error:', auditError);
    }

    if (listRow) {
      store.replaceInAllClients(clientId, row => applyDotNotation(row, updates));
    }
    if (record) {
      if (record.selectedClient) {
        record.selectedClient = keepListRowMark(record.selectedClient, applyDotNotation(record.selectedClient, updates));
      }
      if (record.clientData) record.clientData = applyDotNotation(record.clientData, updates);
    }
    // The portal lists read server-written rows: show the change there now too.
    patchLiveClient(clientId, updates);
    this.notify();

    try {
      const previousClient = previousSelectedClient || previousListRow;
      const mergedOptions = {
        ...(options || {}),
        _workflowContext: {
          fromWorkflowStatus: previousClient?.workflowStatus ?? null,
          fromIsLost: previousClient?.isLost ?? null,
        },
      };
      await updateClientRecord(clientId, updates, userId, mergedOptions);
      return { success: true };
    } catch (error) {
      console.error('Error updating client data:', error);
      dropLiveClientPatch(clientId);
      if (previousListRow) {
        store.replaceInAllClients(clientId, () => previousListRow);
      }
      if (record && store.getRecord(clientId) === record) {
        record.selectedClient = previousSelectedClient;
        record.clientData = previousClientData;
      }
      this.notify();
      return { success: false, error: error.message };
    }
  }

  /**
   * Advice scenarios: a Recommendations / Amendments autosave on a client that
   * has scenarios. Shared paths (Fact Find, `fees.ongoingConsent`, current
   * portfolio) are written as today; the page's scenario paths go to the
   * scenario being edited (adviceScenarioWriter.js) — the client record when it
   * is live, its stored payload otherwise.
   *
   *   scenario: { page, scenarioId, name?, ownAmendmentsFp?, currentAmendmentsFp?, seedAmendmentsFp?, previousData? }
   *   → { success: true, target } | { success: false, error, terminal?, code? }
   * A terminal failure (scenario locked or deleted) must not be retried.
   */
  async updateClientDataGuarded(clientId, rawUpdates, userId = null, scenario = {}) {
    const sc = scenario || {};
    const updates = stampAdviceUpdates(rawUpdates, this.store.getRecord(clientId)?.clientData || null);
    const { scenario: scenarioPaths, shared } = routeFormDataUpdates(sc.page, updates, {
      ownAmendmentsFp: sc.ownAmendmentsFp === true,
      currentAmendmentsFp: sc.currentAmendmentsFp || null,
      seedAmendmentsFp: sc.seedAmendmentsFp === true,
    });
    if (Object.keys(shared).length) {
      const result = await this.updateClientDataForClient(clientId, shared, userId);
      if (!result.success) return result;
    }
    if (!Object.keys(scenarioPaths).length) return { success: true, target: null };

    const cleaned = removeUndefinedValues(scenarioPaths);
    const record = this.store.getRecord(clientId);
    const previousClientData = record?.clientData || null;
    let userName = null;
    if (userId) {
      const userTeamServiceModule = await import('./userTeamService');
      userName = userTeamServiceModule.default.getUserName(userId);
    }
    try {
      // Loaded on first use, so the client store's imports stay free of Firebase.
      const { writeScenarioUpdates } = await import('./adviceScenarioWriter');
      const out = await writeScenarioUpdates({
        clientId, page: sc.page, scenarioId: sc.scenarioId, updates: cleaned, userEmail: userId, userName,
      });
      const live = out.target === 'live';
      try {
        recordMaterialChanges({
          clientId,
          userId,
          updates: cleaned,
          previousData: live ? previousClientData : (sc.previousData || null),
          scenario: { id: out.scenarioId, name: sc.name || null, live },
        });
      } catch (auditError) {
        console.error('Material change audit error:', auditError);
      }
      // The live scenario is the client record: show the change at once, as updateClientDataForClient does.
      if (live) {
        const current = this.store.getRecord(clientId);
        if (current) {
          if (current.selectedClient) current.selectedClient = applyDotNotation(current.selectedClient, cleaned);
          if (current.clientData) current.clientData = applyDotNotation(current.clientData, cleaned);
        }
        this.notify();
      }
      return { success: true, target: out.target };
    } catch (error) {
      if (error && error.terminal) return { success: false, terminal: true, code: error.code, error: error.message };
      console.error('Error saving the advice scenario:', error);
      return { success: false, error: error?.message || 'Save failed' };
    }
  }

  // Check if a client is selected
  hasSelectedClient() {
    return this.selectedClient !== null;
  }

  // Get client display name
  getClientDisplayName() {
    return getClientDisplayNameFor(this.selectedClient);
  }
}

/** Display name for any client document (header tabs, lists). */
export function getClientDisplayNameFor(client) {
  if (!client) return null;
  const personal = client.personal;
  if (personal?.firstName && personal?.surname) {
    return `${personal.firstName} ${personal.surname}`;
  }
  if (personal?.preferredName) {
    return personal.preferredName;
  }
  return 'Unnamed Client';
}

/** A new, empty scope for a workspace tab. Call `dispose()` when the tab closes. */
export function createClientScope() {
  return new ClientStateService(clientStore);
}

// The scope used outside any tab (header, sidebar, services); the active tab's.
let activeScope = carried?.activeScope || createClientScope();

export function getActiveClientScope() {
  return activeScope;
}

export function setActiveClientScope(scope) {
  if (!scope || scope === activeScope) return;
  activeScope = scope;
  clientStore.notify();
}

/** The workspace tab a component renders in; null means "the active tab". */
export const ClientScopeContext = carried?.ClientScopeContext || React.createContext(null);

if (hot) {
  hot.dispose((data) => {
    data.clientState = { clientStore, activeScope, ClientScopeContext };
  });
}

// Default export: always the active tab's scope (services and one-off callers).
const clientStateService = new Proxy({}, {
  get(_, prop) {
    const scope = activeScope;
    const value = scope[prop];
    return typeof value === 'function' ? value.bind(scope) : value;
  },
});

export default clientStateService;

/** Re-render on any client state change in any tab (header tab chips). */
export function useClientStoreVersion() {
  return React.useSyncExternalStore(clientStore.subscribeVersion, clientStore.getVersion);
}

function sameState(a, b) {
  return a.selectedClient === b.selectedClient
    && a.clientData === b.clientData
    && a.allClients === b.allClients
    && a.clientsLoaded === b.clientsLoaded;
}

// Hook for React components to use client state
export const useClientState = () => {
  const contextScope = React.useContext(ClientScopeContext);
  const resolveScope = React.useCallback(() => contextScope || activeScope, [contextScope]);
  const [state, setState] = React.useState(() => resolveScope().getState());

  React.useEffect(() => {
    const update = () => setState(prev => {
      const next = resolveScope().getState();
      return sameState(prev, next) ? prev : next;
    });
    update();
    return clientStore.subscribe(update);
  }, [resolveScope]);

  const scope = resolveScope();
  return {
    ...state,
    selectClient: scope.selectClient.bind(scope),
    clearSelection: scope.clearSelection.bind(scope),
    createNewClient: scope.createNewClient.bind(scope),
    updateClientData: scope.updateClientData.bind(scope),
    updateClientDataForClient: scope.updateClientDataForClient.bind(scope),
    // From the class, not the instance: after a dev hot update, tabs keep scope
    // objects made by the previous copy of this file, which may lack newer methods.
    updateClientDataGuarded: ClientStateService.prototype.updateClientDataGuarded.bind(scope),
    hasSelectedClient: scope.hasSelectedClient.bind(scope),
    getClientDisplayName: scope.getClientDisplayName.bind(scope),
    setAllClients: scope.setAllClients.bind(scope),
    areClientsLoaded: scope.areClientsLoaded.bind(scope),
    removeClientFromCache: scope.removeClientFromCache.bind(scope)
  };
};
